import { useDispatch, useSelector } from 'react-redux';
import axios from  '../utils/axiosConfig';
import { errorHandler } from '../utils/errorHandler';
import {
    setResources,
    setResourcesError,
    setResourcesLoading,
    setShowResourcesEdit,
    setResourcesbyIdsLoading,
    setResourcesbyIdsError,
    setResourcesbyIds,
    setResourcesbySkill,
    setResourcesbySkillLoading,
    setResourcesbySkillError,
    setCapacityPlanningRes,
    setCapacityPlanningLoading,
    setCapacityPlanningError,
    setManageResourcesLoading,
    setManageResourcesError,
    setUnassignedTasksLoading,
    setUnassignedTasksError,
    setUnassignedTasks,
    setResetUnassignedTasks,
    setSuggestedTasksRes,
    setUnassignedAdditionalTasks,
    setSuggestedTasksLoading,
    setSuggestedTasksError
} from '../redux/slices/resources';
import {
    setProfileId,
    setProfileLoading,
    setProfileError
} from '../redux/slices/chat';
import toast from "react-hot-toast";

// Track ongoing requests to prevent duplicates
let resourcesRequestInProgress = false;

interface Resources {
    message: string;
    project_id: string;
    short_description: string;
    success: boolean;
}

const useResourcesService = () => {
    const dispatch = useDispatch();
    const resourcesLoading = useSelector((state: any) => state.resources.loading);
    const manageResourcesLoading = useSelector((state: any) => state.resources.manageResourcesLoading);

    // Helper function to ensure resource IDs are strings
    const ensureResourceIdIsString = (payload: any) => {
        const updatedPayload = {...payload};

        // Handle array of resource IDs
        if (updatedPayload.resource_id && Array.isArray(updatedPayload.resource_id)) {
            updatedPayload.resource_id = updatedPayload.resource_id.map((id: any) => id.toString());
        }
        // Handle single resource ID
        else if (updatedPayload.resource_id) {
            updatedPayload.resource_id = updatedPayload.resource_id.toString();
        }

        return updatedPayload;
    };

    const showError = (error: any) => {
        errorHandler.showError(error);
    };

    const fetchResources = async (org_id:any) => {
        // Check if we already have data or a request is in progress
        if (resourcesLoading || resourcesRequestInProgress) {
            return;  // Skip duplicate requests
        }

        resourcesRequestInProgress = true;
        dispatch(setResourcesLoading(true));
        try {
            const response = await axios.get(`/organization/${org_id}/resources`);
            dispatch(setResources(response?.data?.resources || []));
        } catch (error) {
            showError(error)
            dispatch(setResourcesError(error));
        } finally {
            dispatch(setResourcesLoading(false));
            resourcesRequestInProgress = false;
        }
    };

    const fetchManagerResources = async (project_id:any) => {
        if (manageResourcesLoading || resourcesRequestInProgress) {
            return;  // Skip duplicate requests
        }

        resourcesRequestInProgress = true;
        dispatch(setManageResourcesLoading(true));
        try {
            const response = await axios.get(`/organization/${project_id}/manage_resources`);
            dispatch(setResources(response?.data?.resources || []));
            dispatch(setManageResourcesError(null));  // clear stale list error on success
        } catch (error) {
            showError(error)
            dispatch(setManageResourcesError(error));
        } finally {
            dispatch(setManageResourcesLoading(false));
            resourcesRequestInProgress = false;
        }
    };

    const addResource = async (payload:any) => {
        dispatch(setResourcesLoading(true));
        try {
            const response = await axios.post(`/resources`, payload);
            toast.success(response.data.message);
            fetchManagerResources(payload.org_id);
            dispatch(setShowResourcesEdit(false));
        } catch (error) {
            showError(error)
            dispatch(setResourcesError(error));
        } finally {
            dispatch(setResourcesLoading(false));
        }
    };
    const getResourcesbyIds = async (projectId:any,payload:any) => {
        dispatch(setResourcesbyIdsLoading(true));

        try {
            const updatedPayload = ensureResourceIdIsString(payload);
            const response = await axios.post(`/resources/getResourcesInfo`, updatedPayload);
            dispatch(setResourcesbyIds({[projectId]:response.data}));
        } catch (error) {
            showError(error)
            dispatch(setResourcesbyIdsError(error));
        } finally {
            dispatch(setResourcesbyIdsLoading(false));
        }
    };
    const getRecommendedResources = async (projectId: string, taskId: string) => {
        dispatch(setResourcesbyIdsLoading(true));

        try {
            const response = await axios.get(`/recommendResource/${projectId}/${taskId}`);
            // Store the response with a key that combines project_id and task_id
            const key = `recommendedResources_${projectId}_${taskId}`;
            dispatch(setResourcesbyIds({[key]: response.data}));
        } catch (error) {
            showError(error)
            dispatch(setResourcesbyIdsError(error));
        } finally {
            dispatch(setResourcesbyIdsLoading(false));
        }
    };
    const getResourcesbySkill = async (skill_id:string) => {
        dispatch(setResourcesbySkillLoading(true));
        setResourcesbySkill({})
        try {
            const response = await axios.get(`/resources/getResourcesById/${skill_id}`);
            dispatch(setResourcesbySkill(response.data));
        } catch (error) {
            showError(error)
            dispatch(setResourcesbySkillError(error));
        } finally {
            dispatch(setResourcesbySkillLoading(false));
        }
    };
    const updateResource = async (payload:any) => {
        dispatch(setResourcesLoading(true));
        try {
            const response = await axios.put(`/resources/${payload.id}`, payload);
            toast.success(response.data.message);
            dispatch(setShowResourcesEdit(false));
        } catch (error) {
            showError(error)
            dispatch(setResourcesError(error));
        } finally {
            dispatch(setResourcesLoading(false));
        }
    };

    const fetchCapacityPlanning = async (payload?: {
        start_date: string;
        end_date: string;
        resource_ids: number[];
        filter_type?: string;
        team_name?: string | null;
        project_name?: string | null;
        path?: string | null;
    }) => {
        dispatch(setCapacityPlanningLoading(true));
        try {
            const response = await axios.post(`/resources/capacity_planning`, payload);
            dispatch(setCapacityPlanningRes(response?.data));
        } catch (error) {
            showError(error)
            dispatch(setCapacityPlanningError(error));
        } finally {
            dispatch(setCapacityPlanningLoading(false));
        }
    };

    const fetchCapacityPlanningSprint = async (payload?: {
        sprint_names?: string[] | null;
        teams?: Array<{
            team_name: string;
            project_name: string | null;
            paths: string[] | null;
        }> | null;
        sprint_type?: string | null;
        filters?: {
            skills?: (string | number)[];
            work_item_type?: string[];
        };
    }) => {
        dispatch(setCapacityPlanningLoading(true));
        try {
            const response = await axios.post(`/resources/capacity_planning/sprint`, payload);
            dispatch(setCapacityPlanningRes(response?.data));
        } catch (error) {
            showError(error)
            dispatch(setCapacityPlanningError(error));
        } finally {
            dispatch(setCapacityPlanningLoading(false));
        }
    };

    const getUnassignedTasks = async (payload:any) => {
        dispatch(setUnassignedTasksLoading(true));
        try {
            const updatedPayload = ensureResourceIdIsString(payload);
            const response = await axios.post(`/resources/unassignedTasks`, updatedPayload);
            dispatch(setUnassignedTasks(response?.data?.tasks));
            dispatch(setUnassignedAdditionalTasks(response?.data?.additional_tasks));
        } catch (error) {
            showError(error)
            dispatch(setUnassignedTasksError(error));
        } finally {
            dispatch(setUnassignedTasksLoading(false));
        }
    };
    const saveAssignedTask = async (payload:any,isFetchCapacityPlanning?:boolean) => {
        dispatch(setUnassignedTasksLoading(true));
        try {
            const updatedPayload = ensureResourceIdIsString(payload);
            const response = await axios.post(`/resources/assignTask`, updatedPayload);
            toast.success(response.data.message);
            // fetchCapacityPlanning();
            if(isFetchCapacityPlanning){
                fetchSuggestedTaskOnOpenSlot();
            }
        } catch (error) {
            showError(error)
            dispatch(setUnassignedTasksError(error));
        } finally {
            dispatch(setUnassignedTasksLoading(false));
        }
    };
    const removeAssignedTask = async (payload:any) => {
        dispatch(setUnassignedTasksLoading(true));
        dispatch(setResetUnassignedTasks());
        try {
            const updatedPayload = ensureResourceIdIsString(payload);
            const response = await axios.post(`/resources/removeTask`, updatedPayload);
            toast.success(response.data.message);
            // fetchCapacityPlanning();
        } catch (error) {
            showError(error)
            dispatch(setUnassignedTasksError(error));
        } finally {
            dispatch(setUnassignedTasksLoading(false));
        }
    };

    const fetchSuggestedTaskOnOpenSlot = async () => {
        dispatch(setSuggestedTasksLoading(true));
        try {
            const response = await axios.get<Resources[]>(`/resources/suggest_task_on_open_slot`);
            dispatch(setSuggestedTasksRes(response?.data));
        } catch (error) {
            showError(error)
            dispatch(setSuggestedTasksError(error));
        } finally {
            dispatch(setSuggestedTasksLoading(false));
        }
    };
    const clearSuggestedTaskOnOpenSlot = async () => {
        try {
            dispatch(setSuggestedTasksRes({}));
        } catch (error) {
            showError(error)
        } finally {
        }
    };

    const addTeamsToResource = async (payload: { team_id: number[], resource_id: number }) => {
        try {
            await axios.post(`/resources/add_teams_to_resource`, payload);
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const removeTeamsFromResource = async (payload: { team_id: number[], resource_id: number }) => {
        try {
            await axios.post(`/resources/remove_teams_from_resource`, payload);
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const getUserProfile = async (): Promise<any> => {
        dispatch(setProfileLoading(true));
        dispatch(setProfileError(null));

        try {
            const response = await axios.get(`/users/profile`);
            const data = response?.data;
            const id = data?.id;
            const profileIdValue = typeof id === 'number' ? id : (id != null ? Number(id) : null);
            dispatch(setProfileId(profileIdValue));
            return data;
        } catch (error) {
            showError(error);
            dispatch(setProfileError(error instanceof Error ? error.message : 'Failed to fetch profile'));
            dispatch(setProfileId(null));
            throw error;
        } finally {
            dispatch(setProfileLoading(false));
        }
    };

    const updateUserProfile = async (payload: { first_name?: string; last_name?: string; image_url?: string; enable_user_memory_storage?: boolean; reference_user_chat_history?: boolean; }): Promise<any> => {
        try {
            const response = await axios.put(`/users/profile`, payload);
            toast.success(response?.data?.message || 'Profile updated successfully');
            return response?.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };


    const activateResource = async (org_id: string, resource_id: string) => {
        dispatch(setManageResourcesLoading(true));
        try {
            const response = await axios.put(`/organization/${org_id}/reactivate_resource/${resource_id}`);
            toast.success(response?.data?.message || 'Resource activated successfully');
            await fetchManagerResources(org_id);
            return response.data;
        } catch (error) {
            showError(error);
            // Don't dispatch setManageResourcesError — it's for list-fetch failures and would hide the list.
            throw error;
        } finally {
            dispatch(setManageResourcesLoading(false));
        }
    };

    const deactivateResource = async (org_id: string, resource_id: string) => {
        dispatch(setManageResourcesLoading(true));
        try {
            const response = await axios.put(`/organization/${org_id}/deactivate_resource/${resource_id}`);
            toast.success(response?.data?.message || 'Resource deactivated successfully');
            await fetchManagerResources(org_id);
            return response.data;
        } catch (error) {
            showError(error);
            // Same as activateResource: don't poison the list-level error field.
            throw error;
        } finally {
            dispatch(setManageResourcesLoading(false));
        }
    };

    return {
        fetchResources,
        updateResource,
        addResource,
        getResourcesbyIds,
        getRecommendedResources,
        getResourcesbySkill,
        fetchCapacityPlanning,
        fetchCapacityPlanningSprint,
        getUnassignedTasks,
        saveAssignedTask,
        removeAssignedTask,
        fetchSuggestedTaskOnOpenSlot,
        clearSuggestedTaskOnOpenSlot,
        fetchManagerResources,
        addTeamsToResource,
        removeTeamsFromResource,
        getUserProfile,
        updateUserProfile,
        activateResource,
        deactivateResource,
    };
};

export default useResourcesService;